import { CAMERA, PLANET, PLAYER, SURFACE_FEATURES, VIEW_H, WORLD } from '../domain/content.js';
import type { GameState, ViewModel } from '../contract.js';

export function getViewModel(state: GameState): ViewModel {
  const radialDistance = PLANET.radius + state.player.altitude;
  const upX = Math.cos(state.player.angle);
  const upY = Math.sin(state.player.angle);
  const tangentX = -upY;
  const tangentY = upX;
  const playerX = PLANET.centerX + upX * radialDistance;
  const playerY = PLANET.centerY + upY * radialDistance;
  const cameraUpX = Math.cos(state.cameraAngle);
  const cameraUpY = Math.sin(state.cameraAngle);
  const cameraAltitude = state.cameraMode === 'locked' ? state.player.altitude : 0;
  const cameraDistance = PLANET.radius + cameraAltitude;
  const cameraX = PLANET.centerX + cameraUpX * cameraDistance;
  const cameraY = PLANET.centerY + cameraUpY * cameraDistance;

  return {
    tick: state.tick,
    camera: {
      x: cameraX,
      y: cameraY - (VIEW_H * (0.5 - CAMERA.playerScreenY)) / state.cameraZoom,
      zoom: state.cameraZoom,
      rotation: state.cameraMode === 'locked' ? PLANET.startAngle - state.cameraAngle : 0,
      mode: state.cameraMode,
      deadzoneHalfWidthPx: CAMERA.deadzoneHalfWidthPx,
    },
    planet: {
      x: PLANET.centerX,
      y: PLANET.centerY,
      radius: PLANET.radius,
      atmosphere: PLANET.atmosphere,
      surfaceStroke: PLANET.surfaceStroke,
    },
    player: {
      x: playerX,
      y: playerY,
      angle: state.player.angle,
      altitude: state.player.altitude,
      tangentX,
      tangentY,
      upX,
      upY,
      facing: state.player.facing,
      tangentVelocity: state.player.tangentVelocity,
      radialVelocity: state.player.radialVelocity,
      grounded: state.player.grounded,
      inWater: state.inWater,
      motionState: motionState(state),
      walkCycle: state.player.walkCycle,
      landing01: state.player.landingTicks / PLAYER.landingSquashTicks,
    },
    features: SURFACE_FEATURES.map((feature) => {
      const start = pointAtSurfaceDistance(feature.distance, 0);
      const end = feature.endDistance === undefined ? null : pointAtSurfaceDistance(feature.endDistance, 0);
      const active = feature.kind === 'action'
        ? Math.abs(state.playerSurfaceDistance - feature.distance) <= 100
        : feature.kind === 'water'
          ? state.inWater
          : state.player.blockedTicks > 0;
      const unlocked = feature.id === 'left_learning_stone'
        ? state.discoveries.leftActionSeen
        : feature.id === 'right_minimap_stone'
          ? state.discoveries.rightActionSeen
          : false;
      return {
        id: feature.id,
        kind: feature.kind,
        label: feature.label,
        distance: feature.distance,
        endDistance: feature.endDistance,
        x: start.x,
        y: start.y,
        endX: end?.x,
        endY: end?.y,
        active,
        unlocked,
      };
    }),
    discoveries: {
      minimapUnlocked: state.minimapUnlocked,
      leftActionSeen: state.discoveries.leftActionSeen,
      rightActionSeen: state.discoveries.rightActionSeen,
    },
    world: {
      w: WORLD.w,
      h: WORLD.h,
    },
    diagnostics: {
      speed: Math.abs(state.player.tangentVelocity),
      altitude: state.player.altitude,
      zoom: state.cameraZoom,
      surfaceDistance: state.playerSurfaceDistance,
      swimStamina: state.swimStamina,
    },
    status: {
      kind: state.statusMessage,
      ticks: state.statusTicks,
    },
  };
}

function motionState(state: GameState): ViewModel['player']['motionState'] {
  if (state.inWater) return 'swim';
  if (state.player.blockedTicks > 0) return 'blocked';
  if (state.player.landingTicks > 0) return 'land';
  if (!state.player.grounded) return state.player.radialVelocity >= 0 ? 'jump' : 'fall';
  const speed = Math.abs(state.player.tangentVelocity);
  if (speed < 10 && state.player.idleTicks >= PLAYER.longIdleTicks) return 'idle-long';
  if (speed < 10) return 'idle';
  if (speed < PLAYER.maxGroundSpeed * 0.86) return 'walk';
  return 'run';
}

function pointAtSurfaceDistance(distance: number, altitude: number): { x: number; y: number } {
  const angle = PLANET.startAngle + distance / PLANET.radius;
  const radius = PLANET.radius + altitude;
  return {
    x: PLANET.centerX + Math.cos(angle) * radius,
    y: PLANET.centerY + Math.sin(angle) * radius,
  };
}
